import { capture } from '@parcel/analytics/browser';
import { useQuery } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import * as React from 'react';
import { useCaptureOnce } from '../../lib/analytics.js';
import type { AccountRow } from '../../lib/mock-accounts.js';
import type { ProjectRow } from '../../lib/projects-display.js';
import { type RecordRef, formatRecordParam, useRecordDetail } from '../../lib/record-detail.js';
import { isSupabaseConfigured } from '../../lib/supabase.js';
import { useWatchIntent } from '../../lib/watch-intent.js';
import { getWorkspace, getWorkspaceSlugFromPath } from '../../lib/workspaces.js';
import { AccountDetail } from '../accounts/account-detail-drawer.js';
import { ContactDetail } from '../contacts/contact-detail-drawer.js';
import { ProjectDetail } from '../projects/project-detail-drawer.js';
import type { RecordDetailChromeProps } from './entity-detail-layout.js';
import { type DetailMode, EntityDrawerShell } from './entity-drawer-shell.js';
import { neighborIds, useRecordNavigator } from './record-navigator.js';

const DESKTOP_QUERY = '(min-width: 768px)';

// Expanded mode only exists from md up; below that the sheet is already full width.
function useIsDesktop(): boolean {
  // Read synchronously so a full=1 open below md never paints (or reports) expanded first.
  const [isDesktop, setIsDesktop] = React.useState(() =>
    typeof window.matchMedia === 'function' ? window.matchMedia(DESKTOP_QUERY).matches : true,
  );
  React.useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(DESKTOP_QUERY);
    const sync = () => setIsDesktop(query.matches);
    sync();
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);
  return isDesktop;
}

export function RecordDetailHost() {
  const {
    record,
    expanded,
    followIntent,
    consumeFollowIntent,
    navState,
    open,
    navigateTo,
    close,
    expand,
    collapse,
    back,
  } = useRecordDetail();
  const navigator = useRecordNavigator();
  const isDesktop = useIsDesktop();
  const mode: DetailMode = expanded && isDesktop ? 'expanded' : 'drawer';

  // Keep the last record rendered while the sheet animates closed.
  const lastRecordRef = React.useRef<RecordRef | null>(record);
  if (record) lastRecordRef.current = record;
  const shown = record ?? lastRecordRef.current;

  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const workspaceSlug = getWorkspaceSlugFromPath(pathname);
  const workspaceQuery = useQuery({
    queryKey: ['workspace', workspaceSlug],
    enabled: Boolean(workspaceSlug) && isSupabaseConfigured && Boolean(record),
    queryFn: () => getWorkspace(workspaceSlug ?? ''),
  });
  // A dossier link lands with follow=1; the workspace this host already resolves is the one to follow in.
  useWatchIntent({
    workspaceId: workspaceQuery.data?.id,
    record,
    intent: followIntent,
    consume: consumeFollowIntent,
  });

  // Expanded on the very first render means the URL carried full=1.
  const loadedExpandedRef = React.useRef(expanded);
  const expandedKey = record && mode === 'expanded' ? formatRecordParam(record) : null;
  useCaptureOnce(expandedKey, () => {
    if (!record) return;
    capture('record_expanded', {
      record_type: record.type,
      record_id: record.id,
      source: loadedExpandedRef.current ? 'link' : navState.defaultExpanded ? 'default' : 'button',
    });
    loadedExpandedRef.current = false;
  });

  const { prevId, nextId } = neighborIds(navigator, record);
  const step = (id: string | null) =>
    id && record && navigator
      ? () =>
          open(
            { type: record.type, id },
            { seed: navigator.seedFor(id), source: 'drawer_nav', replace: true },
          )
      : undefined;

  const chrome: RecordDetailChromeProps = {
    mode,
    onToggleMode: mode === 'expanded' ? collapse : expand,
    backLabel: navState.fromLabel,
    onBack: navState.fromLabel ? back : undefined,
    initialTab: navState.tab,
    onPrev: step(prevId),
    onNext: step(nextId),
    onNavigate: navigateTo,
  };
  const source = navState.source ?? 'deep_link';

  return (
    <EntityDrawerShell
      open={Boolean(record)}
      onOpenChange={(isOpen) => {
        if (!isOpen) close();
      }}
      mode={mode}
      onCollapse={collapse}
    >
      {shown ? (
        <div
          key={formatRecordParam(shown)}
          className="flex min-h-0 flex-1 flex-col duration-150 animate-in fade-in-0 motion-reduce:animate-none"
        >
          {shown.type === 'account' ? (
            <AccountDetail
              accountId={shown.id}
              seedAccount={navState.seed as AccountRow | null | undefined}
              source={source}
              {...chrome}
            />
          ) : shown.type === 'contact' ? (
            <ContactDetail
              contactId={shown.id}
              workspaceId={workspaceQuery.data?.id}
              source={source}
              {...chrome}
            />
          ) : (
            <ProjectDetail
              projectId={shown.id}
              seedProject={navState.seed as ProjectRow | null | undefined}
              source={source}
              {...chrome}
            />
          )}
        </div>
      ) : null}
    </EntityDrawerShell>
  );
}
